/**
 * Ticket-entry requestor selection — the pure decision, extracted (OPEN-053).
 *
 * `NewJobModal` is a large Radix modal; testing this rule through it would assert the
 * rendering rather than the rule. These functions hold the whole contract and are unit-tested
 * directly, following the file's existing habit (`collectRequestMetadata`, `buildSiteOptions`).
 *
 * The rule: the dispatcher ticks ONE of the two requestors assigned to the pickup site.
 * Ticking the other REPLACES the first — it is a choice between two people, not a multi-select.
 */

import type { AssignedRequestor, AssignmentSlots, AssignmentTargetType } from './hooks.js';

export interface RequestorChoice {
  readonly slot: 1 | 2;
  readonly requestor: AssignedRequestor;
}

/**
 * Map `NewJobModal`'s pickup selection onto an assignment target.
 *
 * The modal encodes its Site combobox value by prefix — `tb:` tank battery, `wl:` standalone
 * well, `cs:` legacy company_site. Only the first two are assignable: `cs:` points at the old
 * location model, which has no requestor slots and is being retired. Returns `null` for those
 * and for an empty selection, which disables the assignments query entirely.
 */
export function assignmentTargetFor(
  pickupSel: string,
): { targetType: AssignmentTargetType; targetId: string } | null {
  if (pickupSel.startsWith('tb:'))
    return { targetType: 'TANK_BATTERY', targetId: pickupSel.slice(3) };
  if (pickupSel.startsWith('wl:')) return { targetType: 'WELL', targetId: pickupSel.slice(3) };
  return null;
}

/**
 * The requestors a dispatcher may actually pick for this pickup target.
 *
 * Deactivated people are filtered out: they stay visible in the management tab so they can be
 * reactivated, but offering one on a new ticket would attach someone who has left. Order is
 * slot 1 then slot 2, and slot 2 may be present while slot 1 is empty — there is no ordering
 * rule on assignment.
 */
export function selectableRequestors(slots: AssignmentSlots | undefined): RequestorChoice[] {
  if (slots === undefined) return [];
  const out: RequestorChoice[] = [];
  if (slots.slot1 !== null && slots.slot1.isActive) out.push({ slot: 1, requestor: slots.slot1 });
  if (slots.slot2 !== null && slots.slot2.isActive) out.push({ slot: 2, requestor: slots.slot2 });
  return out;
}

/**
 * Apply a click on one requestor's checkbox.
 *
 * Single-select: choosing a different person replaces the current choice rather than adding to
 * it, and clicking the CURRENT choice clears it (so a dispatcher who ticked the wrong box can
 * untick it without reloading the form).
 */
export function toggleChoice(current: string | null, clickedId: string): string | null {
  return current === clickedId ? null : clickedId;
}

/**
 * The requestor part of the POST /v1/jobs body.
 *
 * Returns `{}` — NOT `{ requestorId: null }` — when nothing is chosen. The API body is
 * `.strict()` with an optional uuid, so a literal null would be rejected outright; and
 * "absent" is the honest encoding of "the dispatcher did not pick anyone", which is the normal
 * case for a site with no assignments.
 */
export function requestorPayload(choiceId: string | null): { requestorId?: string } {
  return choiceId === null ? {} : { requestorId: choiceId };
}

/**
 * Whether the free-text requester fields should be locked.
 *
 * When a requestor is chosen the server snapshots THEIR details onto the job, so leaving the
 * inputs editable would show values that are about to be overwritten. With no requestor — the
 * day-one case, and any site without assignments — they stay fully editable, which is the
 * escape hatch that keeps ticket entry working before anyone has populated the table.
 */
export function requesterFieldsLocked(choiceId: string | null): boolean {
  return choiceId !== null;
}
